import { Editor, Element, Node, Range } from '../../core';
import type { Point } from '../../core';
import type { DictationChange } from './types';
import { BRACKET_TYPES } from '../../bracketTypes';
import { isSelectionContainedInNode } from '../../utils/isSelectionContainedInNode';
import { isApplicable } from '../../../ReportCorrections/applyChangesToText';

/** Offsets and JS indexes are both UTF-16; formatting leaves add no separators. */
export function locate(editor: Editor, change: DictationChange): Range | null {
  if (!change.path.length || !Node.has(editor, [...change.path])) return null;
  const block = Node.get(editor, [...change.path]);
  if (!Element.isElement(block) || !Editor.isBlock(editor, block)) return null;
  if (!isApplicable(Node.string(block), change)) return null;

  let offset = 0;
  let anchor: Point | undefined;
  let focus: Point | undefined;
  for (const [leaf, relativePath] of Node.texts(block)) {
    const end = offset + leaf.text.length;
    // A replacement starting at a formatting boundary belongs to the next leaf.
    // Collapsed insertions keep the formatting immediately before the caret.
    if (!anchor && (change.start < end || (change.start === change.end && change.start <= end))) {
      anchor = { path: [...change.path, ...relativePath], offset: change.start - offset };
    }
    if (!focus && change.end <= end) {
      focus = { path: [...change.path, ...relativePath], offset: change.end - offset };
    }
    offset = end;
  }
  if (!anchor || !focus) return null;
  const range = { anchor, focus };
  // Reject inline elements, including zero-width elements at either boundary. Text typed inside a
  // template field's brackets is ordinary text, so a change wholly within one is allowed.
  for (const [element, path] of Editor.nodes(editor, {
    at: [...change.path],
    match: (node) => Element.isElement(node),
  })) {
    if (Element.isElement(element) && Editor.isInline(editor, element)) {
      const inlineRange = Editor.range(editor, path);
      if (Range.intersection(range, inlineRange) === null) continue;
      const isInsideField =
        BRACKET_TYPES.includes(element.type) &&
        !Range.isCollapsed(range) &&
        isSelectionContainedInNode(editor, path, range);
      if (!isInsideField) return null;
    }
  }
  return Editor.string(editor, range) === change.original ? range : null;
}
